import {useCallback, useEffect, useRef, useState} from "react";
import {notificationStore} from "~/stores/init";
import type {DashboardItem} from "./services/dashboardsApi";
import {notifyDashboardError, useDeleteDashboardMutation} from "./services/dashboardsMutations";

/** How long a deleted dashboard can be restored before the DELETE is sent. */
export const DELETE_UNDO_MS = 8000;

type PendingDelete = {dashboard: DashboardItem; timer: ReturnType<typeof setTimeout>; toastId: string};

/**
 * Delete with Undo for your dashboards: `requestDelete` hides the dashboard at once and shows an Undo
 * toast; the DELETE goes out after DELETE_UNDO_MS, or right away when the page unmounts. A failed
 * DELETE brings the dashboard back.
 */
export function useUndoableDashboardDelete(onDeleted: () => Promise<unknown>) {
    const {mutateAsync: deleteDashboard} = useDeleteDashboardMutation();
    const [hiddenIds, setHiddenIds] = useState<ReadonlySet<string>>(new Set());
    const pending = useRef(new Map<string, PendingDelete>());

    const unhide = (id: string) => setHiddenIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
    });

    const commitDelete = useCallback(async (p: PendingDelete) => {
        pending.current.delete(p.dashboard.id);
        try {
            await deleteDashboard({dashboardId: p.dashboard.dashgenId!});
        } catch {
            unhide(p.dashboard.id);
            notifyDashboardError("delete", p.dashboard.id);
            return;
        }
        await onDeleted();
    }, [deleteDashboard, onDeleted]);

    const requestDelete = (d: DashboardItem) => {
        if (d.dashgenId === null || pending.current.has(d.id)) return;
        setHiddenIds(prev => new Set(prev).add(d.id));
        const toastId = `dsh-delete-${d.id}`;
        const entry: PendingDelete = {
            dashboard: d,
            toastId,
            timer: setTimeout(() => void commitDelete(entry), DELETE_UNDO_MS),
        };
        pending.current.set(d.id, entry);
        notificationStore.show(`“${d.title}” deleted.`, {
            id: toastId,
            severity: "info",
            autoHideDuration: DELETE_UNDO_MS,
            actionText: "Undo",
            onAction: () => {
                const p = pending.current.get(d.id);
                if (!p) return;
                clearTimeout(p.timer);
                pending.current.delete(d.id);
                unhide(d.id);
                notificationStore.hide(toastId);
            },
        });
    };

    // Leaving the page commits pending deletes right away instead of dropping them.
    useEffect(() => {
        const map = pending.current;
        return () => {
            map.forEach(p => {
                clearTimeout(p.timer);
                notificationStore.hide(p.toastId);
                void commitDelete(p);
            });
        };
    }, [commitDelete]);

    return {hiddenIds, requestDelete};
}
